import { Component } from '@theme/component';

/**
 * @typedef {Object} PurchaseOptionsRefs
 * @property {HTMLInputElement} sellingPlanInput
 * @property {HTMLInputElement[] | undefined} modeInputs
 * @property {HTMLInputElement[] | undefined} sellingPlanOptionInputs
 * @property {HTMLElement | undefined} sellingPlanGroup
 * @property {HTMLElement | undefined} subscriptionBenefits
 * @property {HTMLElement | undefined} subscriptionPlanLabel
 * @property {HTMLElement | undefined} subscriptionPrice
 * @property {HTMLElement | undefined} subscriptionSavings
 */

class PurchaseOptionsComponent extends Component {
  requiredRefs = ['sellingPlanInput'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#handleChange);
    this.syncState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#handleChange);
  }

  /**
   * Returns the current selection state so product-form can preserve it across variant changes.
   * @returns {{mode: string | null, sellingPlanId: string}}
   */
  getSelectedState() {
    const selectedMode = this.#modeInputs.find((input) => input.checked)?.value || null;
    const selectedPlanId = this.#planInputs.find((input) => input.checked)?.value || '';

    return {
      mode: selectedMode,
      sellingPlanId: selectedPlanId,
    };
  }

  /**
   * Syncs radios, hidden input and summary copy.
   * @param {{mode?: string | null, sellingPlanId?: string} | null} [preferredState]
   */
  syncState(preferredState = null) {
    const modeInputs = this.#modeInputs;
    if (!modeInputs.length) return;

    const hasSubscription = this.#planInputs.length > 0;
    const requiresSellingPlan = this.dataset.requiresSellingPlan === 'true';

    let nextMode = preferredState?.mode || modeInputs.find((input) => input.checked)?.value || null;

    if (nextMode === 'subscription' && !hasSubscription) {
      nextMode = null;
    }

    if (nextMode === 'one-time' && requiresSellingPlan) {
      nextMode = null;
    }

    if (!nextMode) {
      nextMode = hasSubscription ? 'subscription' : 'one-time';
    }

    const nextModeInput = modeInputs.find((input) => input.value === nextMode && !input.disabled) || modeInputs[0];
    if (!nextModeInput) return;

    nextModeInput.checked = true;
    this.dataset.selectedMode = nextModeInput.value;

    this.#syncSellingPlanSelection(preferredState?.sellingPlanId || '');
  }

  get #modeInputs() {
    return Array.isArray(this.refs.modeInputs) ? this.refs.modeInputs : [];
  }

  get #planInputs() {
    return Array.isArray(this.refs.sellingPlanOptionInputs) ? this.refs.sellingPlanOptionInputs : [];
  }

  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (this.#modeInputs.includes(target)) {
      this.syncState();
      return;
    }

    if (this.#planInputs.includes(target)) {
      this.syncState({ mode: 'subscription', sellingPlanId: target.value });
    }
  };

  /**
   * Updates the selected plan, hidden input and summary text.
   * @param {string} preferredPlanId
   */
  #syncSellingPlanSelection(preferredPlanId) {
    const selectedModeInput = this.#modeInputs.find((input) => input.checked);
    const selectedMode = selectedModeInput?.value;
    const planInputs = this.#planInputs;
    const shouldShowSubscriptionDetails = selectedMode === 'subscription' && !selectedModeInput?.disabled;
    const shouldEnablePlans = shouldShowSubscriptionDetails && planInputs.length > 0;

    if (this.refs.sellingPlanGroup instanceof HTMLElement) {
      const hasMultiplePlans = Number(this.dataset.planCount || '0') > 1;
      this.refs.sellingPlanGroup.hidden = !shouldEnablePlans || !hasMultiplePlans;
    }

    if (this.refs.subscriptionBenefits instanceof HTMLElement) {
      this.refs.subscriptionBenefits.hidden = !shouldShowSubscriptionDetails;
    }

    this.refs.sellingPlanInput.disabled = !shouldEnablePlans;

    for (const input of planInputs) {
      input.disabled = !shouldEnablePlans;
    }

    if (!shouldEnablePlans) {
      this.refs.sellingPlanInput.value = '';
      return;
    }

    const selectedPlan =
      planInputs.find((input) => input.value === preferredPlanId) ||
      planInputs.find((input) => input.checked) ||
      planInputs[0];

    if (!selectedPlan) {
      this.refs.sellingPlanInput.value = '';
      return;
    }

    selectedPlan.checked = true;
    this.refs.sellingPlanInput.value = selectedPlan.value;
    this.#updateSubscriptionSummary(selectedPlan);
  }

  /**
   * Updates the subscription card summary with the selected plan details.
   * @param {HTMLInputElement} selectedPlan
   */
  #updateSubscriptionSummary(selectedPlan) {
    if (this.refs.subscriptionPlanLabel instanceof HTMLElement) {
      this.refs.subscriptionPlanLabel.textContent = selectedPlan.dataset.planLabel || '';
    }

    if (this.refs.subscriptionPrice instanceof HTMLElement) {
      this.refs.subscriptionPrice.textContent = selectedPlan.dataset.planPrice || '';
    }

    if (this.refs.subscriptionSavings instanceof HTMLElement) {
      const savings = selectedPlan.dataset.planSavings || '';
      this.refs.subscriptionSavings.textContent = savings;
      this.refs.subscriptionSavings.classList.toggle('hidden', savings === '');
    }
  }
}

if (!customElements.get('purchase-options-component')) {
  customElements.define('purchase-options-component', PurchaseOptionsComponent);
}
